import type { ReactNode } from 'react';
import type { UseDataControlReturn } from 'hooks/useDataControl';
import type { Column } from './columns';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TableHead } from '@/components/ui/table';
import { cn } from 'helpers/cn';
import { columnLabel, columnLabelMobile, FOCUS_RING, sortableColumns } from './columns';

/**
 * A sort control has to say three things and the table head only said one: the static
 * `ArrowUpDown` marked a column sortable but never which column was sorted, nor which
 * way. `sortDirection` was already on the controller and simply unread.
 */
const caretFor = (active: boolean, ascending: boolean) => (!active ? ArrowUpDown : ascending ? ArrowUp : ArrowDown);

type SortHeadCellProps<T> = {
  column: Column<T>;
  controller: UseDataControlReturn<T>;
};

/**
 * The `<th>` for one column.
 *
 * A sortable head is a real `<button>` — it was a bare `<div onClick>` with
 * `cursor-pointer`, so sorting any table in the app was mouse-only, with no tab stop
 * and no Enter/Space. `aria-sort` sits on the `<th>` itself, where assistive tech
 * looks for it; the shadcn table renders native `table`/`tr`/`th`, so the implicit
 * roles it needs are already there.
 *
 * A non-sortable head renders no button and no pointer affordance.
 */
export const SortHeadCell = <T,>({ column, controller }: SortHeadCellProps<T>) => {
  const { sortKey: activeKey, sortDirection, sortBy } = controller;
  const { label, field, sortKey, tooltip, headerClassName } = column;

  const active = !!sortKey && String(activeKey) === String(sortKey);
  const ascending = sortDirection === 'asc';
  const Caret = caretFor(active, ascending);

  const labelText = String(label || field || '');
  const labelTextMobile = String(column.labelMobile || label || field || '');
  const marker = tooltip ? <sup>ⓘ</sup> : '';

  const inner = (
    <>
      {/* Two labels, shown by width: the short one exists for narrow desktop widths. */}
      <span className="hidden min-[500px]:inline">
        {labelText} {marker}
      </span>
      <span className="min-[500px]:hidden">
        {labelTextMobile} {marker}
      </span>
      {sortKey && <Caret className="ml-2 size-4 shrink-0" strokeWidth={1.5} />}
    </>
  );

  const control = sortKey ? (
    <button
      type="button"
      onClick={() => sortBy(sortKey as Extract<keyof T, string> | undefined)}
      aria-label={`Sort by ${labelText || labelTextMobile}`}
      className={cn(
        'micro-label hover:text-foreground flex min-h-7 cursor-pointer items-center rounded-sm text-left',
        'transition-colors duration-150',
        FOCUS_RING,
        active && 'text-foreground',
      )}
    >
      {inner}
    </button>
  ) : (
    // Focusable only when it carries a tooltip, which is otherwise unreachable
    // without a pointer. Still not a control: no pointer cursor, no handler.
    <span
      className={cn('micro-label flex min-h-7 items-center rounded-sm', tooltip && FOCUS_RING)}
      tabIndex={tooltip ? 0 : undefined}
    >
      {inner}
    </span>
  );

  return (
    <TableHead
      className={cn('whitespace-nowrap max-[499px]:px-2', headerClassName)}
      aria-sort={!sortKey ? undefined : !active ? 'none' : ascending ? 'ascending' : 'descending'}
    >
      {tooltip ? <WithTooltip tooltip={tooltip}>{control}</WithTooltip> : control}
    </TableHead>
  );
};

type WithTooltipProps = {
  tooltip: string;
  children: ReactNode;
};

const WithTooltip = ({ tooltip, children }: WithTooltipProps) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>
        <p className="whitespace-pre-line">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

type MobileSortBarProps<T> = {
  columns: Column<T>[];
  controller: UseDataControlReturn<T>;
};

/**
 * Card mode has no column heads to click, so sorting moves to a row of pills above
 * the cards. Same keyboard and focus treatment as the head: real buttons, focus ring,
 * direction reported in the caret and in the accessible name.
 */
export const MobileSortBar = <T,>({ columns, controller }: MobileSortBarProps<T>) => {
  const { sortKey: activeKey, sortDirection, sortBy } = controller;
  const options = sortableColumns(columns);

  if (options.length === 0) return null;

  return (
    <div role="group" aria-label="Sort" className="flex flex-wrap items-center gap-1.5">
      <span className="micro-label">Sort</span>
      {options.map((column) => {
        const key = String(column.sortKey);
        const active = String(activeKey) === key;
        const ascending = sortDirection === 'asc';
        const Caret = caretFor(active, ascending);
        const label = columnLabelMobile(column) || columnLabel(column) || key;
        const direction = active ? (ascending ? ', ascending' : ', descending') : '';

        return (
          <button
            key={key}
            type="button"
            onClick={() => sortBy(column.sortKey as Extract<keyof T, string> | undefined)}
            aria-pressed={active}
            aria-label={`Sort by ${label}${direction}`}
            title={`Sort by ${label}`}
            className={cn(
              // min-h-9: a 36px target, the floor for a thumb.
              'micro-label rounded-pill inline-flex min-h-9 cursor-pointer items-center gap-1.5 border px-2.5',
              'transition-colors duration-150',
              FOCUS_RING,
              active
                ? 'border-sel-line bg-sel-bg text-foreground'
                : // border-input, not border-line: an unselected pill has no fill, so the
                  // outline is the only thing drawing it.
                  'border-input text-dim hover:bg-hover hover:text-foreground',
            )}
          >
            {label}
            <Caret className="size-3 shrink-0" strokeWidth={1.5} />
          </button>
        );
      })}
    </div>
  );
};
